import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { userAPI, authAPI } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { toast } from 'sonner';
import {
  Camera, Bell, BellOff, Moon, Sun, Phone, ArrowLeft, LogOut,
  Trash2, ChevronRight, Shield, Lock, HelpCircle, FileText, Info
} from 'lucide-react';
import { requestNotificationPermission, getNotificationPermission } from '../utils/notifications';

const ProfileSettings = ({ open, onClose }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(getNotificationPermission());
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be less than 5MB'); return; }
    setUploading(true);
    try {
      await userAPI.uploadAvatar(file);
      toast.success('Profile picture updated!');
      window.location.reload();
    } catch { toast.error('Failed to upload profile picture'); }
    finally { setUploading(false); }
  };

  const handleRequestNotifications = async () => {
    const granted = await requestNotificationPermission();
    if (granted) { toast.success('Notifications enabled!'); setNotificationPermission('granted'); }
    else { toast.error('Notification permission denied'); setNotificationPermission('denied'); }
  };

  const handleSavePhone = async () => {
    if (!phoneValue.trim()) return;
    try {
      await userAPI.updatePhone(phoneValue.trim());
      toast.success('Phone updated!');
      setEditingPhone(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await authAPI.deleteAccount();
      toast.success('Account deleted');
      logout();
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
    } finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-white dark:bg-[#0B141A] flex flex-col" data-testid="profile-settings-page">
      {/* Header */}
      <div className="glass-header px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={onClose} className="p-1 -ml-1" data-testid="settings-back-btn">
          <ArrowLeft className="w-5 h-5 text-[#111B21]" />
        </button>
        <h1 className="text-[17px] font-semibold text-[#111B21]">Settings</h1>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Profile Card */}
        <div className="px-4 py-5 flex items-center gap-4 border-b border-[#E9EDEF]" data-testid="profile-card">
          <div className="relative">
            <Avatar className="w-[72px] h-[72px]">
              <AvatarImage src={userAPI.getAvatarUrl(user?.avatar_url)} />
              <AvatarFallback className="bg-[#4338CA] text-white text-2xl">
                {user?.name?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <button
              className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-[#00A884] flex items-center justify-center shadow-md"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-avatar-button"
            >
              <Camera className="w-3.5 h-3.5 text-white" />
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" data-testid="avatar-input" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[17px] font-medium text-[#111B21] truncate" data-testid="settings-user-name">{user?.name}</p>
            <p className="text-[13px] text-[#667781] truncate">{user?.email}</p>
            {uploading && <p className="text-[11px] text-[#00A884] mt-0.5">Uploading...</p>}
          </div>
        </div>

        {/* Phone Number */}
        <div className="px-4 py-3 border-b border-[#E9EDEF]">
          <p className="text-[12px] text-[#00A884] font-medium mb-1.5">Phone</p>
          {editingPhone ? (
            <div className="flex gap-2">
              <Input
                type="tel" placeholder="+919876543210" value={phoneValue}
                onChange={(e) => setPhoneValue(e.target.value)}
                className="h-9 text-[14px] flex-1" autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSavePhone()}
                data-testid="edit-phone-input"
              />
              <Button size="sm" className="bg-[#00A884] hover:bg-[#008f72] h-9 px-4" onClick={handleSavePhone} data-testid="save-phone-button">Save</Button>
              <Button size="sm" variant="ghost" className="h-9" onClick={() => setEditingPhone(false)}>Cancel</Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#667781]" />
                <span className="text-[15px] text-[#111B21]">{user?.phone || 'Not set'}</span>
              </div>
              <button
                onClick={() => { setPhoneValue(user?.phone || ''); setEditingPhone(true); }}
                className="text-[13px] text-[#00A884] font-medium"
                data-testid="edit-phone-button"
              >
                {user?.phone ? 'Change' : 'Add'}
              </button>
            </div>
          )}
        </div>

        {/* Account Section */}
        <SectionHeader label="Account" />
        <SettingsItem
          icon={<Lock className="w-[20px] h-[20px] text-[#667781]" />}
          label="Privacy"
          sub="Last seen, profile photo"
          data-testid="settings-privacy"
        />
        <SettingsItem
          icon={<Shield className="w-[20px] h-[20px] text-[#667781]" />}
          label="Security"
          sub="End-to-end encryption info"
          data-testid="settings-security"
        />

        {/* Appearance */}
        <SectionHeader label="Appearance" />
        <div
          className="flex items-center gap-4 px-5 py-3.5 active:bg-[#F0F2F5] transition-colors cursor-pointer"
          onClick={toggleTheme}
          data-testid="theme-toggle-button"
        >
          <div className="w-[20px] flex items-center justify-center">
            {isDark ? <Moon className="w-[20px] h-[20px] text-[#667781]" /> : <Sun className="w-[20px] h-[20px] text-[#667781]" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] text-[#111B21]">Theme</p>
            <p className="text-[13px] text-[#667781]">{isDark ? 'Dark' : 'Light'}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#8696A0]" />
        </div>

        {/* Notifications */}
        <SectionHeader label="Notifications" />
        <div
          className="flex items-center gap-4 px-5 py-3.5 active:bg-[#F0F2F5] transition-colors cursor-pointer"
          onClick={notificationPermission !== 'granted' ? handleRequestNotifications : undefined}
          data-testid="notification-settings"
        >
          <div className="w-[20px] flex items-center justify-center">
            {notificationPermission === 'granted'
              ? <Bell className="w-[20px] h-[20px] text-[#00A884]" />
              : <BellOff className="w-[20px] h-[20px] text-[#667781]" />
            }
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] text-[#111B21]">Notifications</p>
            <p className="text-[13px] text-[#667781]">
              {notificationPermission === 'granted' ? 'Enabled' : notificationPermission === 'denied' ? 'Blocked — enable in browser' : 'Tap to enable'}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#8696A0]" />
        </div>

        {/* Help & Legal */}
        <SectionHeader label="Help" />
        <SettingsItem
          icon={<HelpCircle className="w-[20px] h-[20px] text-[#667781]" />}
          label="Help Center"
          sub="FAQ, contact us"
          data-testid="settings-help"
        />
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="no-underline">
          <SettingsItem
            icon={<FileText className="w-[20px] h-[20px] text-[#667781]" />}
            label="Privacy Policy"
            data-testid="settings-privacy-policy"
          />
        </a>
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="no-underline">
          <SettingsItem
            icon={<Info className="w-[20px] h-[20px] text-[#667781]" />}
            label="Terms of Service"
            data-testid="settings-terms"
          />
        </a>

        {/* Logout */}
        <div className="mt-2 border-t border-[#E9EDEF]">
          <button
            onClick={handleLogout}
            className="flex items-center gap-4 px-5 py-3.5 w-full active:bg-[#F0F2F5] transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-[20px] h-[20px] text-[#EF4444]" />
            <span className="text-[15px] text-[#EF4444] font-medium">Log out</span>
          </button>
        </div>

        {/* Delete Account */}
        <div className="border-t border-[#E9EDEF]">
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-4 px-5 py-3.5 w-full active:bg-[#F0F2F5] transition-colors"
              data-testid="delete-account-button"
            >
              <Trash2 className="w-[20px] h-[20px] text-[#EF4444]" />
              <span className="text-[15px] text-[#EF4444]">Delete account</span>
            </button>
          ) : (
            <div className="px-5 py-4 space-y-3" data-testid="delete-confirm-section">
              <p className="text-[13px] text-[#667781]">
                This will permanently delete your account, messages, and all data. This action cannot be undone.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm" className="flex-1 h-9"
                  onClick={() => setShowDeleteConfirm(false)}
                  data-testid="delete-cancel-btn"
                >Cancel</Button>
                <Button
                  size="sm" className="flex-1 h-9 bg-[#EF4444] hover:bg-[#DC2626] text-white"
                  onClick={handleDeleteAccount} disabled={deleting}
                  data-testid="delete-confirm-btn"
                >
                  {deleting ? 'Deleting...' : 'Delete my account'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="h-8" />
      </div>
    </div>
  );
};

const SectionHeader = ({ label }) => (
  <div className="px-5 pt-5 pb-1">
    <p className="text-[12px] font-medium text-[#00A884]">{label}</p>
  </div>
);

const SettingsItem = ({ icon, label, sub, ...rest }) => (
  <div className="flex items-center gap-4 px-5 py-3.5 active:bg-[#F0F2F5] transition-colors cursor-pointer" {...rest}>
    <div className="w-[20px] flex items-center justify-center">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-[15px] text-[#111B21]">{label}</p>
      {sub && <p className="text-[13px] text-[#667781]">{sub}</p>}
    </div>
    <ChevronRight className="w-4 h-4 text-[#8696A0]" />
  </div>
);

export default ProfileSettings;
